import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import CaseDownloadPanel from "../components/CaseDownloadPanel";
import StateTransitionPanel from "../components/StateTransitionPanel";
import CaseFlowStepper from "../components/CaseFlowStepper";
import FlowGuide from "../components/FlowGuide";
import AiActivityTimeline from "../components/AiActivityTimeline";
import { N1LiveProgress } from "../components/n1/N1LiveProgress";
import { DiagnosisView } from "../components/n1/DiagnosisView";
import { N2OfferCard } from "../components/n1/N2OfferCard";
import { ReviewHistory } from "../components/n1/ReviewHistory";
import { ComparableAlertsCard } from "../components/n1/ComparableAlertsCard";
import { KitMarketSummary } from "../components/n2/KitMarketSummary";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

const PREV_BADGE = {
  apto: { label: "Apto", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  posiblemente_apto: { label: "Requiere revisión", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  no_apto: { label: "No apto", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  out_of_scope: { label: "Fuera de alcance", cls: "bg-indigo-50 text-indigo-800 border-indigo-300" },
  datos_insuficientes: { label: "Datos incompletos", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

const STATE_LABEL = {
  draft_intake: "Borrador",
  nivel0_pending: "Prevalidación en curso",
  nivel0_ready: "Prevalidación lista",
  out_of_scope: "Fuera de alcance",
  nivel1_payment_pending: "Pendiente pago Diagnóstico",
  nivel1_paid: "Pago Diagnóstico confirmado",
  nivel1_processing: "Procesando Diagnóstico",
  nivel1_needs_human_review: "En revisión humana",
  nivel1_revision_requested: "Revisión solicitada",
  nivel1_delivered: "Diagnóstico entregado",
  nivel2_offer_sent: "Oferta Kit Documental enviada",
  nivel2_payment_pending: "Pendiente pago Kit Documental",
  closed: "Cerrado",
};

export default function CaseDetailPage() {
  const { id } = useParams();
  const [caseDoc, setCaseDoc] = useState(null);
  const [prev, setPrev] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [prevs, setPrevs] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [kits, setKits] = useState([]);
  const [prevalidating, setPrevalidating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(null);
  const [diagRefresh, setDiagRefresh] = useState(0);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setCaseDoc(data);
      try {
        const { data: p } = await api.get(`/cases/${id}/prevalidation`);
        setPrev(p);
      } catch (err) {
        if (process.env.NODE_ENV === "development" && err?.response?.status !== 404) {
          console.warn("Prevalidation load failed:", err);
        }
        setPrev(null);
      }
      // Cargar todas las relaciones de versiones en paralelo
      const [intakesRes, prevsRes, diagsRes, kitsRes, quotaRes] = await Promise.all([
        api.get(`/cases/${id}/intakes`).catch(() => ({ data: { items: [] } })),
        api.get(`/cases/${id}/prevalidations`).catch(() => ({ data: { items: [] } })),
        api.get(`/cases/${id}/diagnoses`).catch(() => ({ data: { items: [] } })),
        api.get(`/cases/${id}/kits`).catch(() => ({ data: { items: [] } })),
        api.get(`/me/quota`).catch(() => ({ data: null })),
      ]);
      setIntakes(intakesRes?.data?.items || []);
      setPrevs(prevsRes?.data?.items || []);
      setDiagnoses(diagsRes?.data?.items || []);
      setKits(kitsRes?.data?.items || []);
      setQuota(quotaRes?.data || null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onN1Finished = useCallback(() => {
    setDiagRefresh((n) => n + 1);
    load();
  }, [load]);

  const onPrevalidateNow = async () => {
    setPrevalidating(true);
    try {
      const { data } = await api.post(`/cases/${id}/submit-intake`);
      const r = data?.prevalidation?.result;
      if (r === "apto") toast.success("APTO para Diagnóstico.");
      else if (r === "posiblemente_apto") toast.message("Prevalidación requiere revisión.");
      else if (r === "no_apto") toast.warning("No apto.");
      else toast.message("Resultado disponible.");
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const msg = formatApiError(e?.response?.data?.detail) || "No se pudo prevalidar";
      if (status === 422 && /l[ií]mite|cuota|quota/i.test(msg)) {
        toast.error(msg);
      } else if (status === 429) {
        toast.error("Has superado el límite de prevalidaciones por hora. " + msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setPrevalidating(false);
    }
  };

  return (
    <div data-testid="case-detail-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to="/dashboard" data-testid="case-detail-back" className="text-sm text-gray-500 hover:text-primary">
            ← Volver al panel
          </Link>
        </div>

        {loading && (
          <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando caso…</div>
        )}

        {!loading && error && (
          <div
            data-testid="case-detail-error"
            className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm"
          >
            {error}
          </div>
        )}

        {!loading && caseDoc && (
          <>
            <div className="mb-8 pb-6 border-b border-gray-200">
              <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
                Caso · {caseDoc.id.slice(0, 8)}
              </p>
              <h1 data-testid="case-detail-title" className="font-serif text-3xl text-gray-900 mb-3">
                {caseDoc.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span
                  data-testid="case-detail-state"
                  className="uppercase tracking-widest border border-primary text-primary px-2 py-[3px]"
                >
                  {STATE_LABEL[caseDoc.state] || caseDoc.state}
                </span>
                {prev && (
                  <span
                    data-testid="case-detail-prev-badge"
                    className={`uppercase tracking-widest border px-2 py-[3px] ${PREV_BADGE[prev.result]?.cls || ""}`}
                  >
                    {PREV_BADGE[prev.result]?.label || prev.result}
                  </span>
                )}
                <span className="text-gray-500">Creado el {formatDate(caseDoc.created_at)}</span>
              </div>
            </div>

            {/* Iter20 — Stepper visual del flujo */}
            <div className="mb-4" data-tour="case-flow-stepper">
              <CaseFlowStepper
                caseId={id}
                state={caseDoc.state}
                timeline={{
                  prevalidation_at: prev?.created_at,
                  diagnosis_at: diagnoses?.[0]?.delivered_at || diagnoses?.[0]?.created_at,
                  documents_at: kits?.[0]?.delivered_at || kits?.[0]?.created_at,
                }}
              />
            </div>

            {/* Iter25 — Guía de auto-orientación: detecta el nivel activo por estado */}
            <div className="mb-6">
              <FlowGuide
                level={
                  /^nivel2/.test(caseDoc.state) ? "n2" :
                  /^nivel1/.test(caseDoc.state) ? "n1" : "n0"
                }
                quota={quota}
                state={caseDoc.state}
                caseId={id}
              />
            </div>

            {/* Transiciones contextuales del caso (rol usuario) */}
            <div className="mb-6 border border-gray-200 bg-gray-50 rounded-sm p-4">
              <StateTransitionPanel
                caseId={id}
                currentState={caseDoc.state}
                onTransitioned={() => load()}
                endpoint="/cases"
              />
            </div>

            <div className="border border-gray-200 bg-white">
              <dl className="divide-y divide-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Objeto contractual</dt>
                  <dd className="md:col-span-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {caseDoc.objeto_contractual}
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Valor estimado</dt>
                  <dd className="md:col-span-2 font-serif text-xl text-gray-900">
                    {formatCOP(caseDoc.valor_estimado_cop)}
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Última actualización</dt>
                  <dd className="md:col-span-2 text-sm text-gray-800">{formatDate(caseDoc.updated_at)}</dd>
                </div>
              </dl>
            </div>

            {/* Fase 12 Sprint 2: Borrador del precontrato (versiones) */}
            <div
              className="mt-6 border border-gray-200 bg-white p-6"
              data-testid="case-detail-draft-block"
            >
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-gray-500">Borrador del precontrato</p>
                  <p className="text-sm text-gray-700">
                    Versión actual: <strong>v{intakes.find((i) => i.is_current)?.version || 1}</strong>
                    {intakes.length > 1 && (
                      <span className="text-gray-500"> · Total versiones: {intakes.length}</span>
                    )}
                  </p>
                </div>
                <Button asChild variant="outline" className="rounded-sm h-9" data-testid="case-detail-edit-draft">
                  <Link to={`/cases/${id}/edit-draft`}>Editar borrador</Link>
                </Button>
              </div>
              {intakes.length > 1 && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-primary">
                    Ver historial de versiones ({intakes.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs">
                    {intakes.map((iv) => (
                      <li key={iv.id} className="flex items-center gap-2" data-testid={`intake-version-${iv.version}`}>
                        <span className="font-mono text-gray-700">v{iv.version}</span>
                        {iv.is_current && (
                          <span className="bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 text-[9px] uppercase">
                            Actual
                          </span>
                        )}
                        <span className="text-gray-500">— {formatDate(iv.created_at)}</span>
                        {iv.prevalidations_count > 0 && (
                          <span className="text-[10px] text-gray-600">
                            · {iv.prevalidations_count} prevalidación{iv.prevalidations_count !== 1 ? "es" : ""}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            {/* Fase 12 Sprint 2 — Fase E: Cadena de versiones */}
            {(prevs.length > 0 || diagnoses.length > 0 || kits.length > 0) && (
              <div
                className="mt-6 border border-gray-200 bg-white p-6"
                data-testid="case-detail-version-chain"
              >
                <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">
                  Cadena de versiones
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b">
                        <th className="text-left py-2 pr-3">Tipo</th>
                        <th className="text-left py-2 pr-3">Versión</th>
                        <th className="text-left py-2 pr-3">Estado</th>
                        <th className="text-left py-2 pr-3">Basado en</th>
                        <th className="text-left py-2 pr-3">Siguiente paso</th>
                        <th className="text-right py-2">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prevs.map((p) => (
                        <tr
                          key={`prev-${p.id}`}
                          className="border-b border-gray-100"
                          data-testid={`chain-prev-${p.version}`}
                        >
                          <td className="py-2 pr-3 text-gray-700">Prevalidación</td>
                          <td className="py-2 pr-3 font-mono">
                            v{p.version}
                            {p.is_current && (
                              <span className="ml-1 text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-1">
                                Actual
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`px-1.5 py-0.5 text-[10px] uppercase border ${
                              p.result === "apto" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                              : p.result === "posiblemente_apto" ? "bg-amber-50 text-amber-900 border-amber-300"
                              : p.result === "no_apto" ? "bg-rose-50 text-rose-800 border-rose-300"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                            }`}>
                              {p.result}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-gray-600">
                            {p.intake_version_used ? `Borrador v${p.intake_version_used}` : "—"}
                          </td>
                          <td className="py-2 pr-3">
                            {p.has_diagnosis ? (
                              <span className="text-[10px] uppercase bg-indigo-50 text-indigo-800 border border-indigo-300 px-1.5 py-0.5">
                                Diagnosticado ({p.diagnoses_generated.map((d) => `v${d.version}`).join(", ")})
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Sin diagnóstico</span>
                            )}
                          </td>
                          <td className="py-2 text-right text-gray-500">{formatDate(p.created_at)}</td>
                        </tr>
                      ))}
                      {diagnoses.map((d) => (
                        <tr
                          key={`diag-${d.id}`}
                          className="border-b border-gray-100"
                          data-testid={`chain-diag-${d.version}`}
                        >
                          <td className="py-2 pr-3 text-gray-700">Diagnóstico</td>
                          <td className="py-2 pr-3 font-mono">
                            v{d.version}
                            {d.is_current && (
                              <span className="ml-1 text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-1">
                                Actual
                              </span>
                            )}
                            {d.auto_variant && (
                              <span className="ml-1 text-[9px] uppercase bg-amber-50 text-amber-900 border border-amber-300 px-1">
                                Auto
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`px-1.5 py-0.5 text-[10px] uppercase border ${
                              d.human_review_status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                              : d.human_review_status === "skipped_by_user_auto_variant" ? "bg-amber-50 text-amber-900 border-amber-300"
                              : d.human_review_status === "changes_requested" ? "bg-rose-50 text-rose-800 border-rose-300"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                            }`}>
                              {d.human_review_status || "—"}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-gray-600">
                            {d.prevalidation_version_used ? `Prevalid. v${d.prevalidation_version_used}` : "—"}
                            {d.intake_version_used ? ` · Borrador v${d.intake_version_used}` : ""}
                          </td>
                          <td className="py-2 pr-3">
                            {d.has_kit ? (
                              <span className="text-[10px] uppercase bg-violet-50 text-violet-800 border border-violet-300 px-1.5 py-0.5">
                                Kit ({d.kits_generated.map((k) => `v${k.version}`).join(", ")})
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Sin kit</span>
                            )}
                          </td>
                          <td className="py-2 text-right text-gray-500">{formatDate(d.created_at)}</td>
                        </tr>
                      ))}
                      {kits.map((k) => (
                        <tr
                          key={`kit-${k.id}`}
                          className="border-b border-gray-100"
                          data-testid={`chain-kit-${k.version}`}
                        >
                          <td className="py-2 pr-3 text-gray-700">Kit Documental</td>
                          <td className="py-2 pr-3 font-mono">
                            v{k.version}
                            {k.is_current && (
                              <span className="ml-1 text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-1">
                                Actual
                              </span>
                            )}
                            {k.auto_variant && (
                              <span className="ml-1 text-[9px] uppercase bg-amber-50 text-amber-900 border border-amber-300 px-1">
                                Auto
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`px-1.5 py-0.5 text-[10px] uppercase border ${
                              k.human_review_status === "approved" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                              : k.human_review_status === "skipped_by_user_auto_variant" ? "bg-amber-50 text-amber-900 border-amber-300"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                            }`}>
                              {k.human_review_status || "—"}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-gray-600">
                            {k.diagnosis_version_used ? `Diagnóstico v${k.diagnosis_version_used}` : "—"}
                            {k.n0_intake_version_used ? ` · Borrador v${k.n0_intake_version_used}` : ""}
                          </td>
                          <td className="py-2 pr-3 text-gray-400 italic">—</td>
                          <td className="py-2 text-right text-gray-500">{formatDate(k.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Prevalidation summary / CTA */}
            {prev ? (
              <div
                data-testid="case-detail-prev-summary"
                className="mt-6 border border-gray-200 bg-white p-6"
              >
                <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
                  Prevalidación · v{prev.version || 1}
                  {prev.intake_version_used && (
                    <span className="text-gray-400"> · sobre borrador v{prev.intake_version_used}</span>
                  )}
                </p>
                <p className="text-sm text-gray-800 mb-4 line-clamp-3">{prev.reason}</p>
                <Button asChild className="rounded-sm" data-testid="case-detail-view-prev">
                  <Link to={`/cases/${id}/prevalidation`}>Ver prevalidación completa</Link>
                </Button>
              </div>
            ) : (
              <div
                data-testid="case-detail-no-prev"
                className="mt-6 border border-primary/30 bg-primary/[0.02] p-6"
              >
                <p className="text-[11px] uppercase tracking-widest text-primary font-medium mb-2">
                  Prevalidación pendiente
                </p>
                <p className="text-sm text-gray-700 mb-4">
                  Tu borrador aún no se ha enviado al motor de prevalidación. Puedes prevalidar ahora
                  (consume 1 cuota gratuita) o editar el borrador antes.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={onPrevalidateNow}
                    disabled={prevalidating || caseDoc.state !== "draft_intake"}
                    className="rounded-sm"
                    data-testid="case-detail-cta-prevalidate"
                  >
                    {prevalidating ? "Prevalidando…" : "Prevalidar ahora"}
                  </Button>
                  <Button asChild variant="outline" className="rounded-sm">
                    <Link to={`/cases/${id}/edit-draft`}>Editar borrador antes</Link>
                  </Button>
                </div>
              </div>
            )}

            <N1LiveProgress caseId={id} state={caseDoc.state} onFinished={onN1Finished} />
            <DiagnosisView caseId={id} refreshKey={diagRefresh} onRegenerated={load} />
            <N2OfferCard caseId={id} state={caseDoc.state} isOwner={user?.id === caseDoc.user_id} />
            <KitMarketSummary caseId={id} />
            <ComparableAlertsCard caseId={id} isOwner={user?.id === caseDoc.user_id} />
            <ReviewHistory caseId={id} refreshKey={diagRefresh} />

            {/* Phase 7: download + Kit Documental CTA */}
            <CaseDownloadPanel caseDoc={caseDoc} onCaseChanged={setCaseDoc} />

            {/* Iter25 — Timeline IA: lo que la IA hizo en TU caso (N1 si hay diagnóstico, N2 si hay kit) */}
            {kits?.[0] && (
              <div className="mt-6">
                <AiActivityTimeline
                  level="n2"
                  artifact={kits[0]}
                  extra={{
                    comparables_count: kits[0]?.comparables_count_used,
                    reviewer_email: caseDoc?.assigned_reviewer_email,
                    checklist_completed: kits[0]?.checklist_passed_count,
                    checklist_total: 10,
                    normas: kits[0]?.validated_output?.normas_aplicadas || kits[0]?.normas_aplicadas || [],
                  }}
                />
              </div>
            )}
            {!kits?.[0] && diagnoses?.[0] && (
              <div className="mt-6">
                <AiActivityTimeline
                  level="n1"
                  artifact={diagnoses[0]}
                  extra={{
                    comparables_count: diagnoses[0]?.comparables_count_used,
                    reviewer_email: caseDoc?.assigned_reviewer_email,
                    normas: diagnoses[0]?.validated_output?.normas_aplicadas || diagnoses[0]?.normas_aplicadas || [],
                  }}
                />
              </div>
            )}
            {!kits?.[0] && !diagnoses?.[0] && prev && (
              <div className="mt-6">
                <AiActivityTimeline level="n0" artifact={prev} />
              </div>
            )}

            <div className="mt-8">
              <Disclaimer dataTestId="case-detail-disclaimer" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
